import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
// Without this, every @mantine/charts component in the app renders unstyled: ChartLegend
// loses its `display:flex` and collapses into a tall vertical stack, and tooltips render as
// transparent divs. It affects the Project, Task-report and Procurement charts, which have
// been quietly broken. (Budget's charts are built on raw Recharts and never depended on it.)
import '@mantine/charts/styles.css';
import { useEnrichedAuth } from './hooks/useEnrichedAuth';
import { usePreferenceLoader } from './hooks/usePreferenceLoader';
import { ProtectedRoute } from './components/ccms-components/auth/components/ProtectedRoute';
import { LoadingScreen } from './components/common/LoadingScreen';

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { OnboardingLayout } from './layouts/OnboardingLayout';

// Pages
import LoginPage from './pages/auth/login';
import ForgotPasswordPage from './pages/auth/forgot-password';
import ResetPasswordPage from './pages/auth/reset-password';
import OnboardingPage from './pages/auth/onboarding';
import PlanningCentreCallbackPage from './pages/auth/planning-centre-callback';
import MainPage from './pages';
import AccessDenied from './pages/AccessDenied';
import SettingsPage from './pages/settings';
import ConnectedAppsPage from './pages/settings/connected-apps';
import StreamingTestPage from './pages/public/StreamingTest';
import PrivacyPolicyPage from './pages/public/PrivacyPolicy';
import TermsOfServicePage from './pages/public/TermsOfService';
import CookiesPolicyPage from './pages/public/CookiesPolicy';
import DataDeletionRequestPage from './pages/public/DataDeletionRequest';
import AccessibilityPage from './pages/public/Accessibility';
import SupportPage from './pages/public/Support';
import MarketingPage from './pages/public/Marketing';

// Routes
import {
  AdminRoutes,
  MainRoutes,
  DemoRoutes,
  TasksRoutes,
  ProjectsRoutes,
  ProcurementsRoutes,
  MeetingsRoutes,
  MembersRoutes,
  LocationsRoutes,
  EventsRoutes,
  DepartmentsRoutes,
  FeedbackRoutes,
  ResourcesRoutes,
  SocialMediaRoutes,
  AnatomyRoutes,
  VendorsRoutes, 
  StoreRoutes,
  AnnouncementsRoutes,
  BookingsRoutes,
  BudgetsRoutes,
  FormsRoutes,
  RidesRoutes,
  ProceduresRoutes,
  CallCentreRoutes,
  SalvationRoutes,
  AssetManagementRoutes,
  QrCodesRoutes,
  AiRoutes,
  AppsRoutes,
  AdminMembersRoutes,
  AdminHelpRoutes
} from './routes';

// Public form pages (anonymous) — lazy so the fill page stays data-light.
const FormFillPage = lazy(() => import('./pages/public/forms/FormFillPage'));
const FormDonePage = lazy(() => import('./pages/public/forms/FormDonePage'));
const FormManageEntryPage = lazy(() => import('./pages/public/forms/FormManageEntryPage'));

function App() {
  const { isAuthenticated, user, isLoading, isInitializing } = useEnrichedAuth();
  const { hasLocalPreferences, isLoadingPreferences } = usePreferenceLoader();
  const location = useLocation();

  // Show loading screen while auth is initializing/loading OR preferences are loading.
  // Guarding on isInitializing is essential: the SDK starts with isLoading=false but
  // isInitializing=true, and without this check protected routes would momentarily see
  // !isAuthenticated and redirect to /auth/login, destroying any deep-linked URL.
  if (isLoading || isInitializing || (isAuthenticated && isLoadingPreferences)) {
    return <LoadingScreen />;
  }

  // Post-login redirect target: honor ?redirect=... on /auth/login so deep links survive.
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const postLoginTarget = redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
    ? redirectParam
    : '/';

  // Force onboarding if:
  // 1. User has NOT completed onboarding, OR
  // 2. User HAS completed onboarding BUT has no local preferences
  //    (handles new device login without server preferences)
  const requiresOnboarding = isAuthenticated && user && (
    !user.onboardingCompleted ||
    (user.onboardingCompleted && !hasLocalPreferences)
  );

  return (
    <>
      <Routes>
        <Route>
          {/* Public Routes - Redirect based on auth state */}
          <Route
          path="/auth/login"
          element={
            isAuthenticated ? (
              requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <Navigate to={postLoginTarget} replace />
              )
            ) : (
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            )
          }
        />
        <Route
          path="/auth/forgot-password"
          element={
            isAuthenticated ? (
              requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <Navigate to={postLoginTarget} replace />
              )
            ) : (
              <AuthLayout showBackButton backTo="/auth/login">
                <ForgotPasswordPage />
              </AuthLayout>
            )
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            isAuthenticated ? (
              requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <Navigate to={postLoginTarget} replace />
              )
            ) : (
              <AuthLayout>
                <ResetPasswordPage />
              </AuthLayout>
            )
          }
        />
        <Route
          path="/auth/oauth/planning-centre/callback"
          element={
            <AuthLayout>
              <PlanningCentreCallbackPage />
            </AuthLayout>
          }
        />

        {/* Public Test Route - No auth required */}
        <Route
          path="/test/streaming"
          element={<StreamingTestPage />}
        />

        {/* Public Legal Pages - No auth required (used as App Store / Play policy URLs) */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/cookies" element={<CookiesPolicyPage />} />

        {/* Public App Store / Play Store pages - No auth required, not linked from login */}
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/marketing" element={<MarketingPage />} />

        {/* Public Data-Deletion (POPIA erasure) request - No auth required */}
        <Route
          path="/data-deletion"
          element={
            <AuthLayout showBackButton backTo="/auth/login">
              <DataDeletionRequestPage />
            </AuthLayout>
          }
        />

        {/* Public form pages (Forms Management) - No auth required. JWT is optional:
            a signed-in member gets prefill, everyone else fills anonymously. */}
        <Route
          path="/f/e/:ref"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <FormManageEntryPage />
            </Suspense>
          }
        />
        <Route
          path="/f/:code/done"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <FormDonePage />
            </Suspense>
          }
        />
        <Route
          path="/f/:code"
          element={
            <Suspense fallback={<LoadingScreen />}>
              <FormFillPage />
            </Suspense>
          }
        />

        {/* Onboarding Route - Protected but before full app access */}
        <Route
          path="/auth/onboarding"
          element={
            <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
              <OnboardingLayout>
                <OnboardingPage />
              </OnboardingLayout>
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Require auth AND onboarding complete */}

        {/* Admin member directory: the Admin role OR the Member.Read right (checked inside
            AdminMembersRoutes; ProtectedRoute ANDs roles with permissions). More specific than
            /admin/*, so React Router picks it first. */}
        <Route
          path="/admin/members/*"
          element={
            <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AdminMembersRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Help content editor: the Admin role OR the Help.Content.Manage right (checked inside
            AdminHelpRoutes). More specific than /admin/*, so React Router picks it first. */}
        <Route
          path="/admin/help/*"
          element={
            <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AdminHelpRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Admin Routes - Protected and require Admin role */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute
              requireAuth
              requiredRoles={['Admin']}
              fallback={<AccessDenied />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AdminRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Demo Routes - Protected */}
        <Route
          path="/app/demo/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <DemoRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Tasks Routes - Protected */}
        <Route
          path="/app/tasks/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <TasksRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Projects Routes - Protected */}
        <Route
          path="/app/projects/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <ProjectsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Procurements Routes - Protected */}
        <Route
          path="/app/procurements/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <ProcurementsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Budgets Routes - Protected */}
        <Route
          path="/app/budgets/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <BudgetsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Forms Routes - Protected */}
        <Route
          path="/app/forms/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <FormsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Rides Routes - Protected */}
        <Route
          path="/app/rides/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <RidesRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Procedures Routes - Protected */}
        <Route
          path="/app/procedures/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <ProceduresRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Call Centre ("Call campaigns") Routes - Protected */}
        <Route
          path="/app/callcentre/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <CallCentreRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Salvation Team Routes - Protected */}
        <Route
          path="/app/salvation/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <SalvationRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Meetings Routes - Protected */}
        <Route
          path="/app/meetings/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <MeetingsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Members Routes - Protected */}
        <Route
          path="/app/members/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <MembersRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Locations Routes - Protected */}
        <Route
          path="/app/locations/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <LocationsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Events Routes - Protected */}
        <Route
          path="/app/events/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <EventsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Departments Routes - Protected */}
        <Route
          path="/app/departments/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <DepartmentsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Feedback Routes - Protected */}
        <Route
          path="/app/feedback/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <FeedbackRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Resources Routes - Protected */}
        <Route
          path="/app/resources/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <ResourcesRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Social Media Routes - Protected */}
        <Route
          path="/app/social-media/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <SocialMediaRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Anatomy (Acts API) integration */}
        <Route
          path="/app/anatomy/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AnatomyRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Vendors Routes - Protected */}
        <Route
          path="/app/vendors/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <VendorsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Announcements Routes - Protected */}
        <Route
          path="/app/announcements/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AnnouncementsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Bookings Routes - Protected (Staff + BookingAdmin) */}
        <Route
          path="/app/bookings/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <BookingsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Asset Management Routes - Protected (admin asset register) */}
        <Route
          path="/app/assetmanagement/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AssetManagementRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* QR Codes Routes - Protected */}
        <Route
          path="/app/qr-codes/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <QrCodesRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* AI Routes - Protected */}
        <Route
          path="/app/ai/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AiRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Get the Apps - Protected. The CCMS mobile apps are distributed privately
            (you cannot find them by searching the App Store), so this page is where a
            user learns how to install them and collects the redemption code an admin
            assigned them. */}
        <Route
          path="/app/apps/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <AppsRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Store Routes - Protected */}
        <Route
          path="/app/store/*"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <StoreRoutes />
              )}
            </ProtectedRoute>
          }
        />

        {/* Main Routes - Profile, Settings, etc. */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <MainRoutes />
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <SettingsPage />
              )}
            </ProtectedRoute>
          }
        />

        {/* The user's LLM connector sessions (see and disconnect). */}
        <Route
          path="/settings/connected-apps"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <ConnectedAppsPage />
              )}
            </ProtectedRoute>
          }
        />

        {/* Homepage - Domain Launcher */}
        <Route
          path="/"
          element={
            <ProtectedRoute
              requireAuth
              fallback={<Navigate to="/auth/login" replace />}
            >
              {requiresOnboarding ? (
                <Navigate to="/auth/onboarding" replace />
              ) : (
                <MainPage />
              )}
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect based on auth state */}
        <Route
          path="*"
          element={
            <Navigate
              to={
                isAuthenticated
                  ? requiresOnboarding
                    ? '/auth/onboarding'
                    : '/'
                  : `/auth/login?redirect=${encodeURIComponent(
                      `${location.pathname}${location.search}${location.hash}`
                    )}`
              }
              replace
            />
          }
        />
        </Route>
      </Routes>
    </>
  );
}

export default App;
